import React, {useEffect, useRef, useState} from "react";
import Cue from "../../Cue";
import {STROKE} from "../../stroke";
import {useGameStore} from "../../features/game/game-store";
import {describeBallOn, RULESET_NAMES} from "../../shared/rules";
import "./Hud.scss";
import {useInputMode} from "../../inputModeState";
import {BALL_IN_HAND_HINT, clockSeconds} from "../../match/hud";
import TouchControls from "./TouchControls";

/** The ball-in-hand hint names the keys; on a touch screen it names the pad instead. */
export const BALL_IN_HAND_TOUCH_HINT = "Ball in hand: use the arrow pad to place the cue ball in the D, then play your shot.";

const BALL_COLOURS: Record<string, string> = {
    red: "#c0141e",
    // Any ball: the colours of the numbered set (Balls.ts POOL_COLOURS)
    any: "conic-gradient(#f5c400 0 12.5%, #1b4fb8 0 25%, #d0202a 0 37.5%, #5a2a86 0 50%, #f07a14 0 62.5%, #0c7a3c 0 75%, #7b1d24 0 87.5%, #121212 0)",
    colour: "conic-gradient(#f2c200 0 16%, #0b7a3b 0 33%, #7a4a1c 0 50%, #1446a8 0 66%, #f28cb1 0 83%, #151515 0)",
    yellow: "#f2c200", green: "#0b7a3b", brown: "#7a4a1c", blue: "#1446a8", pink: "#f28cb1", black: "#151515",
};

export function BallDot({ball}: {ball: string}) {
    return <span className="ball-dot" style={{background: BALL_COLOURS[ball] || "#888"}}/>;
}

function turnText(phase: string, mode: string, myTurn: boolean, opponent: string) {
    if (phase === "over") return "Frame over";
    if (mode === "practice") return phase === "shooting" ? "Balls rolling…" : "Your shot";
    if (phase === "spectating") return `${opponent} is shooting…`;
    if (phase === "waiting") return "Waiting for the referee…";
    if (phase === "shooting") return "Balls rolling…";
    return myTurn ? "Your turn" : `${opponent} to play`;
}

/** Subtle stroke-speed meter: visible while Space is held, shows the last shot's power briefly. */
function PowerMeter() {
    const box = useRef<HTMLDivElement>(null);
    const fill = useRef<HTMLDivElement>(null);
    const hint = useRef<HTMLDivElement>(null);
    useEffect(() => {
        let raf = 0, shown = -1;
        const update = () => {
            const now = performance.now();
            const last = Cue.LastShot || {speed: 0, t: -Infinity};
            const recent = now - last.t < 1500;
            const active = Cue.Enabled && Cue.StrokeActive;
            // the stroke sensitivity multiplier is already in the measured cue speed
            const speed = recent ? last.speed : active ? Cue.LiveSpeed(now) : 0;
            // an unarmed push was stopped short of the ball: say why nothing happened
            const blocked = active && !recent && !!(Cue.Tracker && Cue.Tracker.blocked(now));
            const visible = active || recent;
            if (hint.current) hint.current.style.opacity = blocked ? "1" : "0";
            if (box.current) {
                box.current.style.opacity = visible ? "1" : "0";
                box.current.classList.toggle("blocked", blocked);
                box.current.classList.toggle("armed", (active && !!(Cue.Tracker && Cue.Tracker.armed)) || recent);
            }
            // square-root scale so soft and normal strokes are readable; full bar = the speed cap
            const pct = Math.round(Math.sqrt(Math.min(1, speed / STROKE.MAX_SPEED)) * 100);
            if (pct !== shown) {
                shown = pct;
                if (fill.current) fill.current.style.height = pct + "%";
            }
        };
        const tick = () => { raf = requestAnimationFrame(tick); update(); };
        raf = requestAnimationFrame(tick);
        // also update synchronously on every stroke event, so the armed state never lags a slow frame
        Cue.StrokeListeners.add(update);
        return () => { cancelAnimationFrame(raf); Cue.StrokeListeners.delete(update); };
    }, []);
    return <div className="power-meter" ref={box} aria-hidden="true" title="Stroke speed">
        <div className="pull-back-hint" ref={hint}>Pull back first</div>
        <div className="track"><div className="fill" ref={fill}/></div>
    </div>;
}

/**
 * Ranked frames: the shot clock of the player at the table, counting down to 0 (it keeps running while that player is
 * disconnected). Hidden while a shot is played (the server stops the clock) and outside ranked frames.
 */
export function ShotClock({mine, name}: {mine: boolean, name: string}) {
    const clock = useGameStore(s => s.clock);
    // Only the whole seconds are state, so the clock re-renders (and repaints) once a second, not on every tick:
    // continuous repaints over the WebGL canvas are expensive on slow / software-rendered machines.
    const [secs, setSecs] = useState(() => clock ? clockSeconds(clock.deadline, Date.now()) : 0);
    useEffect(() => {
        if (!clock) return;
        const tick = () => setSecs(clockSeconds(clock.deadline, Date.now()));
        tick();
        const id = window.setInterval(tick, 250);
        return () => window.clearInterval(id);
    }, [clock]);
    if (!clock) return null;
    const urgent = secs <= 10;
    const pct = Math.min(100, Math.max(0, secs * 1000 / clock.totalMs * 100));
    return <div id="shot-clock" className={"shot-clock" + (urgent ? " urgent" : "") + (mine ? " mine" : "")}
                role="timer" aria-label={`${mine ? "Your" : name + "'s"} shot clock: ${secs} seconds`} data-seconds={secs}>
        <span className="label">{mine ? "Your shot" : name}</span>
        <span className="secs">{Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}</span>
        <span className="bar"><span className="fill" style={{width: pct + "%"}}/></span>
    </div>;
}

export default function Hud() {
    const mode = useGameStore(s => s.mode);
    const players = useGameStore(s => s.players);
    const turn = useGameStore(s => s.turn);
    const you = useGameStore(s => s.you);
    const phase = useGameStore(s => s.phase);
    const breakPoints = useGameStore(s => s.breakPoints);
    const ballOn = useGameStore(s => s.ballOn);
    const redsLeft = useGameStore(s => s.redsLeft);
    const ballsLeft = useGameStore(s => s.ballsLeft);
    const ruleset = useGameStore(s => s.ruleset);
    const suddenDeath = useGameStore(s => s.suddenDeath);
    const anyball = ruleset === "anyball";
    const hint = useGameStore(s => s.hint);
    const toasts = useGameStore(s => s.toasts);
    const status = useGameStore(s => s.status);
    const ranked = useGameStore(s => s.ranked);
    const clockTurn = useGameStore(s => s.clock?.turn ?? null);
    const [locked, setLocked] = useState<boolean>(!!document.pointerLockElement);
    useEffect(() => {
        const onChange = () => setLocked(!!document.pointerLockElement);
        document.addEventListener("pointerlockchange", onChange);
        return () => document.removeEventListener("pointerlockchange", onChange);
    }, []);

    const touch = useInputMode() === "touch";
    const online = mode === "online";
    const myTurn = !online || turn === you;
    const opponent = online ? (players[1 - you]?.name || "Opponent") : "";

    return <div className={"hud" + (touch ? " hud-touch" : "")} aria-live="polite">
        <div className="scoreboard">
            {online ? players.map((p, i) =>
                <div key={i} style={{order: i === 0 ? 0 : 2}} className={"player" + (turn === i && phase !== "over" ? " at-table" : "") + (i === you ? " me" : "")}>
                    <div className="name">
                        {p.name}{i === you ? " (you)" : ""}{p.guest ? <span className="tag">guest</span> : null}
                        {!p.connected ? <span className="tag warn">offline</span> : null}
                    </div>
                    <div className="score">{p.score}</div>
                    <div className="hb">HB {p.highestBreak}</div>
                </div>
            ) : <div className="player at-table me">
                <div className="name">Practice{anyball ? <span className="tag">{RULESET_NAMES.anyball}</span> : null}</div>
                <div className="score">{players[0]?.score ?? 0}</div>
                <div className="hb">HB {players[0]?.highestBreak ?? 0}</div>
            </div>}
            <div className="centre">
                <div className="turn">{turnText(phase, mode, myTurn, opponent)}</div>
                <div className="details">
                    {online && ranked ? <span className="ranked-tag" id="ranked-tag">Ranked</span> : null}
                    {anyball && online ? <span className="ruleset" data-ruleset="anyball">{RULESET_NAMES.anyball}</span> : null}
                    {suddenDeath && phase !== "over" ? <span className="sudden-death"><b>Sudden death</b></span> : null}
                    <span>{anyball ? "Run" : "Break"} <b>{breakPoints}</b></span>
                    <span>Ball on <BallDot ball={ballOn}/> <b>{describeBallOn(ballOn)}</b></span>
                    {anyball ? <span>Balls <b>{ballsLeft}</b></span> : <span>Reds <b>{redsLeft}</b></span>}
                </div>
                {online && ranked && status === "playing" && clockTurn != null
                    ? <ShotClock mine={clockTurn === you} name={players[clockTurn]?.name || "Opponent"}/> : null}
            </div>
        </div>
        <div className="toasts">
            {toasts.map(t => <div key={t.id} className={"toast-msg " + t.kind}>{t.text}</div>)}
        </div>
        {!touch && !locked && status === "playing" && phase === "aiming" && myTurn
            ? <div className="aim-hint">Click the table to aim. Move the mouse to look around, hold Space and stroke the mouse back and forward to shoot. Press O for Settings, or Esc to release the mouse for the menu.</div>
            : hint ? <div className="hint">{touch && hint === BALL_IN_HAND_HINT ? BALL_IN_HAND_TOUCH_HINT : hint}</div> : null}
        <PowerMeter/>
        {touch ? <TouchControls/> : null}
    </div>;
}
